import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useCompleteUserProfile } from '@/hooks/useCompleteUserProfile';
import { useUsernameValidation } from '@/hooks/useUsernameValidation';
import { useUserSubscriptionStatus } from '@/hooks/useUserSubscriptionStatus';
import { useUserIdentities } from '@/hooks/useUserIdentities';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { AlertTriangle, Trash2, Info, Loader2, CheckCircle, AlertCircle, Link2, Unlink, Mail } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CategorySelect } from '@/components/auth/FinalizeRegistrationForm/CategorySelect';
import { genders } from '@/components/auth/FinalizeRegistrationForm/PersonalInfoSection';
import { useCategories } from '@/hooks/useCategories';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';

// Age ranges from database enum
const ageRanges = [
  "Under 18", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"
] as const;

const ProfileManagement = () => {
  const { user, deleteUser } = useAuth();
  const { currentCommunity } = useCommunity();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Data hooks
  const { profile, loading: profileLoading, error: profileError, refetch } = useCompleteUserProfile();
  const { data: subscriptionInfo, isLoading: subscriptionLoading } = useUserSubscriptionStatus();
  const { isChecking: usernameChecking, isUnique: usernameUnique, error: usernameError, checkUsername } = useUsernameValidation();
  const { 
    identities, 
    loading: identitiesLoading, 
    linking, 
    unlinking, 
    linkNewGoogleAccount, 
    unlinkAccount, 
    refetchIdentities,
    checkLinkingStatus,
    canUnlink 
  } = useUserIdentities();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [gender, setGender] = useState<"Male" | "Female" | ''>('');
  const [ageRange, setAgeRange] = useState<typeof ageRanges[number] | ''>('');
  const [country, setCountry] = useState('');
  const [town, setTown] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState<boolean | null>(null);
  const [categoryInterests, setCategoryInterests] = useState<string[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Fetch categories for interests using the same hook as registration
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  
  const categories = useMemo(() => {
    if (!categoriesData?.categories) return [];
    return categoriesData.categories.map(category => ({
      id: category.id,
      name: category.name || category.translation_key || 'Unnamed category'
    }));
  }, [categoriesData]);

  // Handle OAuth redirect for identity linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linked = params.get('linked');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    
    console.log('[ProfileManagement] OAuth redirect params:', { linked, error, errorDescription });
    
    if (linked === 'success') {
      toast({
        title: "Account linked successfully",
        description: "Your Google account has been linked",
      });
      // Clean URL
      navigate('/profile', { replace: true });
      // Refresh identities list
      refetchIdentities();
    } else if (linked === 'error' || error) {
      let errorMessage = "There was an error linking your Google account.";
      
      if (error === 'server_error' && errorDescription?.includes('already linked')) {
        errorMessage = "This Google account is already linked to your profile.";
      } else if (error === 'already_linked') {
        errorMessage = "This Google account is already linked to another user.";
      } else if (error === 'same_identity') {
        errorMessage = "This Google account is already linked to your profile.";
      } else if (error === 'manual_linking_disabled') {
        errorMessage = "Manual linking is not enabled for this application.";
      } else if (errorDescription) {
        errorMessage = errorDescription;
      }
      
      console.log('[ProfileManagement] OAuth error:', error, errorDescription);
      
      toast({
        title: "Failed to link account",
        description: errorMessage,
        variant: "destructive"
      });
      navigate('/profile', { replace: true });
    }
    
    // Check if we were in the middle of linking
    checkLinkingStatus();
  }, [location.search, navigate, toast, refetchIdentities, checkLinkingStatus]);

  // Populate form from profile data
  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstname || '');
      setLastName(profile.lastname || '');
      setUsername(profile.username || '');
      setGender(profile.gender || '');
      setAgeRange(profile.age_range || '');
      setCountry(profile.country || '');
      setTown(profile.town || '');
      setPostalCode(profile.postal_code || '');
      setMarketingOptIn(profile.marketing_opt_in);
      setCategoryInterests(profile.categoryInterests || []);
    }
  }, [profile]);

  // Username validation
  useEffect(() => {
    if (username && username !== profile?.username) {
      checkUsername(username, profile?.username || undefined);
    }
  }, [username, profile?.username, checkUsername]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !currentCommunity?.id) return;
    
    // Validate username if changed
    if (username !== profile?.username && (!usernameUnique || usernameError)) {
      return;
    }

    setLoading(true);
    
    try {
      // Update user_profiles
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          firstname: firstName,
          lastname: lastName,
          gender: gender || null,
          age_range: ageRange || null,
          country: country || null,
          town: town || null,
          postal_code: postalCode?.toUpperCase() || null,
          marketing_opt_in: marketingOptIn,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Update community_members (username)
      if (username !== profile?.username) {
        const { error: usernameError } = await supabase
          .from('community_members')
          .update({ username })
          .eq('user_id', user.id)
          .eq('community_id', currentCommunity.id);

        if (usernameError) throw usernameError;
      }

      // Update category interests
      // First, delete existing preferences
      const { error: deleteError } = await supabase
        .from('user_preferred_categories')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Then, insert new preferences
      if (categoryInterests.length > 0) {
        const categoryInserts = categoryInterests.map(categoryId => ({
          user_id: user.id,
          category_id: categoryId
        }));

        const { error: insertError } = await supabase
          .from('user_preferred_categories')
          .insert(categoryInserts);

        if (insertError) throw insertError;
      }

      // Refetch the profile data
      await refetch();
      
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== user?.email) return;
    
    setIsDeletingAccount(true);
    try {
      const result = await deleteUser(deleteConfirmation);
      if (result.error) {
        console.error('Failed to delete account:', result.error);
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Delete account error:', error);
    } finally {
      setIsDeletingAccount(false);
      setDeleteConfirmation('');
    }
  };

  if (profileLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <Card className="rounded-2xl shadow-lg border-0">
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin mr-3" />
            <span className="text-lg">Loading profile...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <Card className="rounded-2xl shadow-lg border-0">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading profile: {profileError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">Profile Settings</h1>
        <p className="text-sm lg:text-base text-muted-foreground hidden lg:block">Update your personal information and preferences</p>
      </div>
      <Card className="rounded-2xl shadow-lg border-0">
        <CardContent className="p-4 sm:p-6 lg:p-8 xl:p-12">

          <form onSubmit={handleUpdateProfile} className="space-y-8">
            {/* Personal Information Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold mb-6">
                Personal Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="flex items-center gap-1">
                    First Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    required
                    className="h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="flex items-center gap-1">
                    Last Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    required
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="username" className="flex items-center gap-1">
                    Username <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>This will be used in chats for non-business community members.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="relative">
                    <Input
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Choose a username"
                      required
                      minLength={3}
                      className="h-12"
                    />
                    {usernameChecking && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {!usernameChecking && username && username !== profile?.username && usernameUnique === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                    )}
                    {!usernameChecking && username && username !== profile?.username && usernameUnique === false && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      </div>
                    )}
                  </div>
                  {usernameError && <div className="text-xs text-destructive mt-1">{usernameError}</div>}
                  {usernameChecking && <div className="text-xs text-muted-foreground mt-1">Checking availability...</div>}
                  {!usernameChecking && username && username !== profile?.username && usernameUnique === true && (
                    <div className="text-xs text-green-500 mt-1">Username is available!</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    Your username will be displayed in chat conversations with business owners instead of your full name for privacy.
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender" className="flex items-center gap-1">
                    Gender <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>We require your gender for demographic analytics.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <Select value={gender} onValueChange={(value: "Male" | "Female") => setGender(value)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select gender..." />
                    </SelectTrigger>
                    <SelectContent>
                      {genders.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="mt-6">
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  <strong>Your email:</strong> {user?.email}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold">
                Address Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="country" className="flex items-center gap-1">
                    Country <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>Your country helps us customize your experience.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Country"
                    className="h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="town" className="flex items-center gap-1">
                    City <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>We need your town to suggest local services.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    id="town"
                    value={town}
                    onChange={(e) => setTown(e.target.value)}
                    placeholder="City"
                    className="h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="postalCode" className="flex items-center gap-1">
                    Postcode <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>Helps to refine your location for recommendations.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <Input
                    id="postalCode"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="Postcode"
                    className="h-12"
                  />
                </div>
              </div>
            </div>

            {/* Preferences Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold">
                Preferences
              </h3>
              
              <div className="space-y-6">
                <div className="max-w-md">
                  <Label htmlFor="ageRange" className="flex items-center gap-1">
                    Age Range <span className="text-destructive">*</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>For recommendations (e.g., youth/elderly events).</TooltipContent>
                    </Tooltip>
                  </Label>
                  <Select value={ageRange} onValueChange={(value: typeof ageRanges[number]) => setAgeRange(value)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select age range..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ageRanges.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="flex items-center gap-1 mb-3">
                    What interests you most?
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>Select categories you are interested in.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <CategorySelect
                    categories={categories}
                    selected={categoryInterests}
                    onChange={setCategoryInterests}
                    isLoading={categoriesLoading}
                  />
                </div>
                
                <div className="max-w-md">
                  <Label className="flex items-center gap-1 mb-3">
                    Keep me updated about community news and offers
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 flex items-center"><Info size={16} /></span>
                      </TooltipTrigger>
                      <TooltipContent>We may send you occasional relevant offers &amp; updates.</TooltipContent>
                    </Tooltip>
                  </Label>
                  <RadioGroup
                    className="flex flex-row gap-8 pt-2"
                    value={marketingOptIn === true ? "yes" : marketingOptIn === false ? "no" : ""}
                    onValueChange={(v) => setMarketingOptIn(v === "yes")}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="marketingOptIn-yes" />
                      <label htmlFor="marketingOptIn-yes" className="text-sm">Yes</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="marketingOptIn-no" />
                      <label htmlFor="marketingOptIn-no" className="text-sm">No</label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </div>

            {/* Save Changes Button */}
            <div className="flex justify-end pt-8">
              <Button 
                type="submit" 
                disabled={loading || usernameChecking || (username !== profile?.username && usernameUnique !== true)} 
                size="lg"
                className="px-8"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>

          {/* Linked Accounts Section */}
          <div className="mt-8 lg:mt-12 pt-6 lg:pt-8 border-t border-border">
            <div className="space-y-4 lg:space-y-6">
              <div>
                <h3 className="text-lg lg:text-xl font-semibold mb-2">Linked Accounts</h3>
                <p className="text-sm text-muted-foreground">
                  Manage your linked Google accounts for authentication
                </p>
              </div>

              {identitiesLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading linked accounts...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {identities.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">
                      No linked accounts found. This shouldn't happen - please refresh the page.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {identities.map((identity) => (
                        <div 
                          key={identity.id} 
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-muted/30 rounded-lg border border-border"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <Mail className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium mb-1">
                                {identity.provider === 'google' ? 'Google Account' : identity.provider}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">
                                {identity.identity_data?.email || 'No email available'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex justify-end sm:justify-start">
                            {canUnlink && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    disabled={unlinking === identity.id}
                                    className="w-full sm:w-auto"
                                  >
                                    {unlinking === identity.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                        <span className="hidden sm:inline">Unlinking...</span>
                                        <span className="sm:hidden">...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Unlink className="mr-2 h-3 w-3" />
                                        Unlink
                                      </>
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="mx-4 max-w-md">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Unlink Google Account</AlertDialogTitle>
                                    <AlertDialogDescription className="break-words">
                                      Are you sure you want to unlink this Google account? 
                                      <br />
                                      <span className="font-medium break-all text-xs mt-1 block">
                                        {identity.identity_data?.email}
                                      </span>
                                      <br />
                                      You'll still be able to sign in with your other linked accounts.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
                                    <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => unlinkAccount(identity)}
                                      className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Unlink Account
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            
                            {!canUnlink && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    disabled
                                    className="w-full sm:w-auto"
                                  >
                                    <Unlink className="mr-2 h-3 w-3" />
                                    Unlink
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>You must have at least one linked account</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-4">
                    <Button 
                      onClick={linkNewGoogleAccount}
                      disabled={linking}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {linking ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span className="hidden sm:inline">Connecting...</span>
                          <span className="sm:hidden">Connecting...</span>
                        </>
                      ) : (
                        <>
                          <Link2 className="mr-2 h-4 w-4" />
                          <span className="hidden sm:inline">Connect Google Account</span>
                          <span className="sm:hidden">Connect Google Account</span>
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Link a different Google account. This will open Google's account picker so you can choose a different account to link.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger Zone Section */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="bg-red-50 rounded-xl p-6 md:p-8 border border-destructive/20">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <h3 className="text-xl font-semibold text-destructive">Danger Zone</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                This action is permanent and cannot be undone. Please be certain before proceeding.
              </p>
              
              {subscriptionLoading ? (
                <p className="text-muted-foreground text-sm">
                  Checking account status...
                </p>
              ) : (
                <>
                  {subscriptionInfo?.belongsToMultipleCommunities ? (
                    <div className="space-y-4">
                      <p className="text-muted-foreground text-sm">
                        Your account is associated with multiple communities. 
                        To ensure proper data handling across all communities, please contact your community administrator 
                        to process your account deletion request.
                      </p>
                      {currentCommunity?.admin_contact_email && (
                        <div className="bg-muted p-4 rounded-lg">
                          <p className="text-muted-foreground text-sm mb-1">Contact administrator:</p>
                          <a 
                            href={`mailto:${currentCommunity.admin_contact_email}`} 
                            className="text-primary hover:underline text-sm font-medium"
                          >
                            {currentCommunity.admin_contact_email}
                          </a>
                        </div>
                      )}
                      <Button variant="destructive" disabled className="opacity-50 cursor-not-allowed">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Account
                      </Button>
                    </div>
                  ) : subscriptionInfo?.hasActiveBusinessSubscriptions ? (
                    <div className="space-y-4">
                      <p className="text-muted-foreground text-sm">
                        You cannot delete your account while you have businesses with active subscriptions. 
                        Please cancel your subscriptions first. Canceled subscriptions can be deleted immediately.
                      </p>
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>Active Subscriptions:</strong> {subscriptionInfo.businessesWithActiveSubscriptions.length} business(es)
                        </p>
                      </div>
                      <Button variant="destructive" disabled className="opacity-50 cursor-not-allowed">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Account
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-muted-foreground text-sm">
                        Deleting your account will permanently remove all your information, businesses, reviews, and other data. 
                        This action cannot be undone.
                      </p>
                      
                      <AlertDialog onOpenChange={() => setDeleteConfirmation('')}>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Account
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirm Account Deletion</AlertDialogTitle>
                            <AlertDialogDescription className="space-y-4">
                              <p>
                                This will permanently delete your account and all associated data. This action cannot be undone.
                              </p>
                              
                              <div className="bg-muted p-3 rounded-md my-3">
                                <p className="font-medium">To confirm deletion, please type your email address:</p>
                                <p className="font-bold mt-1 break-words">{user?.email}</p>
                              </div>
                              
                              <Input
                                type="email"
                                placeholder="Type your email address to confirm"
                                value={deleteConfirmation}
                                onChange={(e) => setDeleteConfirmation(e.target.value)}
                                className="mt-3"
                              />
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDeleteAccount}
                              disabled={deleteConfirmation !== user?.email || isDeletingAccount}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {isDeletingAccount ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                'Delete Account'
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileManagement;